/**
 * @fileoverview Payment template tokens on a purchase surface.
 * @module screen-view-v2/utils/paymentTokens
 *
 * W7 fold-in. `substituteVariables` leaves an unknown `@reference` untouched (FR-059),
 * which is right for a copy screen and wrong for a purchase surface: `ProductCatalog`
 * picks `itemVariablesMap?.[item.productId] || variables`, so when the baked prices are
 * missing the literal `@price` reaches the card the user is asked to buy.
 *
 * The rule this module enforces: on a purchase surface an unresolved payment token renders
 * as **nothing**, and the runtime says out loud when a token the card cannot do without is
 * missing. A blank price is a visible bug the operator can see and fix; `@price` on a card
 * is a lie about what the customer is agreeing to pay.
 */

import type { PreviewMode } from '../types/previewMode';
import type { TemplateVariables } from './templateParser';

/**
 * Tokens a priced card cannot do without. A product that resolves none of these is
 * misconfigured, and the runtime says so.
 */
export const REQUIRED_PRICE_TOKENS = [
  'price',
  'priceAndCurrency',
  'priceandcurrency',
  'duration',
  'interval',
] as const;

/**
 * Promotional tokens. A perfectly healthy product with no trial and no introductory offer
 * resolves NONE of these — `useV2Purchase` writes them as empty strings — so an unresolved
 * one is normal and must never warn. It is still blanked if it is missing entirely, because
 * a literal `@trialDays` on a card is as wrong as a literal `@price`.
 */
export const OPTIONAL_PROMO_TOKENS = [
  'trialDays',
  'fullTrialDays',
  'introPrice',
  'introProduct',
  'introDuration',
] as const;

export const PAYMENT_TEMPLATE_TOKENS = [
  ...REQUIRED_PRICE_TOKENS,
  ...OPTIONAL_PROMO_TOKENS,
] as const;

/** Products already reported, so a re-render does not repeat the warning. */
const warnedProductIds = new Set<string>();

export interface ProductTemplateVariablesInput {
  /** Authoring previews retain unresolved tags; omitted mode is live runtime. */
  previewMode?: PreviewMode;
  /** `ProductOffer.productId` — may be absent on a malformed catalogue item. */
  productId?: string;
  /** Per-product baked prices, when the runtime has them. */
  itemVariablesMap?: Record<string, TemplateVariables>;
  /** Screen-level variables, used when the product has no row of its own. */
  variables?: TemplateVariables;
}

/** True when the value would still substitute — i.e. it carries an actual price fact. */
function isResolved(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * True when the slot would leak a raw `@token` or whitespace to the card.
 *
 * An empty string is NOT in this set: `''` is a token the runtime resolved to "this product
 * has none", it already renders as nothing, and treating it as a change would hand back a
 * fresh object on every render and defeat the memoised `ProductOfferItem`.
 */
function needsBlanking(value: unknown): boolean {
  if (typeof value !== 'string') return true;
  return value.length > 0 && value.trim().length === 0;
}

/**
 * Template variables for one product card. Authoring previews retain tags; live
 * purchase surfaces blank unresolvable payment tokens.
 *
 * Returns the source object **unchanged** when nothing needed blanking, so a healthy
 * catalogue keeps its referential identity and the memoised rows do not re-render.
 */
export function resolveProductTemplateVariables(
  input: ProductTemplateVariablesInput,
): TemplateVariables {
  const { productId, itemVariablesMap, variables } = input;
  const source = (productId && itemVariablesMap?.[productId]) || variables || {};

  if (input.previewMode === 'edit' || input.previewMode === 'static') return source;

  const toBlank = PAYMENT_TEMPLATE_TOKENS.filter((token) => needsBlanking(source[token]));
  const unresolvedRequired = REQUIRED_PRICE_TOKENS.filter((token) => !isResolved(source[token]));

  if (unresolvedRequired.length > 0) {
    const key = productId ?? '(no productId)';
    if (!warnedProductIds.has(key)) {
      warnedProductIds.add(key);
      console.warn(
        `[ProductCatalog] product '${key}' has no resolved ${unresolvedRequired.join(', ')} — ` +
          'the slots render blank rather than showing the raw template token. ' +
          'Check paymentConfig.preBakedPrices for this product and locale.',
      );
    }
  }

  if (toBlank.length === 0) {
    return source;
  }

  const blanked: TemplateVariables = { ...source };
  for (const token of toBlank) {
    blanked[token] = '';
  }
  return blanked;
}
